/**
 * CAPEX variance table (SCM pack — SCM7).
 *
 * Compares the BoM-priced CAPEX (sum of `bomItem.totalCostUsd` grouped
 * by category) against the uploaded financial model's
 * `capexTotalUsd` and surfaces per-category subtotals plus a total
 * delta with an amber flag when variance ≥ 10%.
 *
 * Pure presentational component — accepts the structured table from
 * `buildCapexVarianceTable` so the data layer stays test-friendly.
 *
 * Mount point: anywhere in the Financial tab — orchestrator decides
 * at merge time. SCM-UI's `SupplyChainPanel.Overview` may also embed
 * this component; the props are minimal on purpose so both surfaces
 * can render identical content.
 *
 * TODO(orchestrator): SCM-Data will land the canonical
 * `src/types/supplyChain.ts`. This component imports from the local
 * stub today — orchestrator's swap is transparent.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type {
  CapexVarianceTable as CapexVarianceTableData,
  CapexVarianceRow,
} from '../../../lib/itemizedCapex';

export interface CapexVarianceTableProps {
  data: CapexVarianceTableData;
  /** Optional caption rendered above the table. */
  caption?: string;
}

export function CapexVarianceTable({
  data,
  caption,
}: CapexVarianceTableProps): React.JSX.Element {
  const { rows, totalRow, unpricedItemCount, pricedItemCount } = data;
  const hasModelComparison = totalRow.modelUsd != null;

  return (
    <div className="space-y-2" data-testid="capex-variance-table">
      {caption && (
        <div className="text-[11px] font-semibold uppercase tracking-widest text-text-primary">
          {caption}
        </div>
      )}
      <table className="w-full text-[12px] border border-border">
        <thead>
          <tr className="text-text-muted border-b border-border">
            <th className="text-left px-2 py-1.5 font-medium">Category</th>
            {hasModelComparison && (
              <th className="text-right px-2 py-1.5 font-medium">Model</th>
            )}
            <th className="text-right px-2 py-1.5 font-medium">BoM-derived</th>
            {hasModelComparison && (
              <>
                <th className="text-right px-2 py-1.5 font-medium">Δ USD</th>
                <th className="text-right px-2 py-1.5 font-medium">Δ %</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={hasModelComparison ? 5 : 2}
                className="px-2 py-3 text-center text-text-muted text-[11px]"
              >
                No priced BoM items yet — variance unavailable.
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <Row key={row.category} row={row} hasModelComparison={hasModelComparison} />
          ))}
          <tr className="border-t border-border font-semibold bg-state-hover">
            <Row row={totalRow} hasModelComparison={hasModelComparison} asTotal />
          </tr>
        </tbody>
      </table>
      <div className="text-[10px] text-text-muted flex items-center gap-3">
        <span>{pricedItemCount} priced item{pricedItemCount === 1 ? '' : 's'}</span>
        {unpricedItemCount > 0 && (
          <span className="text-warning">
            {unpricedItemCount} unpriced item{unpricedItemCount === 1 ? '' : 's'} excluded
          </span>
        )}
      </div>
    </div>
  );
}

interface RowProps {
  row: CapexVarianceRow;
  hasModelComparison: boolean;
  asTotal?: boolean;
}

function Row({ row, hasModelComparison, asTotal }: RowProps): React.JSX.Element {
  // The total row is rendered inside a <tr> with bg styling already
  // applied; here we render the cells inline.
  const cells = (
    <>
      <td className="px-2 py-1.5 text-left">{row.label}</td>
      {hasModelComparison && (
        <td className="px-2 py-1.5 text-right tabular-nums">
          {formatUsd(row.modelUsd)}
        </td>
      )}
      <td className="px-2 py-1.5 text-right tabular-nums">
        {formatUsd(row.bomUsd)}
      </td>
      {hasModelComparison && (
        <>
          <td className="px-2 py-1.5 text-right tabular-nums">
            {formatDeltaUsd(row.deltaUsd)}
          </td>
          <td
            className={`px-2 py-1.5 text-right tabular-nums ${row.flagged ? 'text-warning' : ''}`}
          >
            <span className="inline-flex items-center gap-1">
              {row.flagged && <AlertTriangle className="w-3 h-3" aria-hidden />}
              {formatDeltaPct(row.deltaPct)}
            </span>
          </td>
        </>
      )}
    </>
  );
  if (asTotal) return cells;
  return <tr className="border-t border-border">{cells}</tr>;
}

function formatUsd(value: number | null): string {
  if (value == null) return '—';
  const millions = value / 1_000_000;
  if (Math.abs(millions) >= 100) {
    return `$${millions.toFixed(0)}M`;
  }
  return `$${millions.toFixed(1)}M`;
}

function formatDeltaUsd(value: number | null): string {
  if (value == null) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const millions = Math.abs(value) / 1_000_000;
  if (millions >= 100) {
    return `${sign}$${millions.toFixed(0)}M`;
  }
  return `${sign}$${millions.toFixed(1)}M`;
}

function formatDeltaPct(value: number | null): string {
  if (value == null) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(1)}%`;
}
